/**
 * @fileoverview
 *
 * A Stimulus controller to manage a tag-based multi-select input, often used
 * with an autocomplete component.
 *
 * It displays selected items as visual tags, allows their removal, and syncs
 * the selected values to hidden form fields for submission. It supports both
 * single and multiple selection modes.
 *
 * @example - Multiple selection (default)
 *
 * <div
 *   data-controller="element-tag-input"
 *   data-element-tag-input-field-name-value="screenplay[character_names][]"
 *   data-element-tag-input-selected-value='["JOHN", "MARY"]'
 * >
 *   <div data-element-tag-input-target="tagsContainer" class="flex flex-wrap gap-2 mb-2"></div>
 *   <input type="text" data-element-tag-input-target="input" data-action="autocomplete:select->element-tag-input#handleSelect">
 *   <div data-element-tag-input-target="hiddenFields"></div>
 * </div>
 *
 * @example - Single selection
 *
 * <div
 *   data-controller="element-tag-input"
 *   data-element-tag-input-field-name-value="screenplay[protagonist_name]"
 *   data-element-tag-input-multiple-value="false"
 * >
 *   <!-- ... targets ... -->
 * </div>
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    // Array of currently selected item names (e.g., ["JOHN", "MARY"])
    selected: Array,
    // Whether to allow multiple selections. If false, a new selection replaces the old one.
    multiple: { type: Boolean, default: true },
    // The name attribute for the hidden form fields.
    fieldName: String,
  };

  static targets = [
    // The text input element, typically used by an autocomplete controller.
    "input",
    // The container where tag badges are rendered.
    "tagsContainer",
    // The container where hidden input fields are managed for form submission.
    "hiddenFields",
  ];

  connect() {
    this.render();
  }

  /**
   * Stimulus value callback that triggers a re-render whenever the `selected`
   * array changes.
   */
  selectedValueChanged() {
    this.render();
    this.updateParentCountBadge();
  }

  /**
   * Updates the count badge in the parent disclosure component.
   * This ensures the badge stays in sync when tags are added/removed
   * via autocomplete or direct manipulation.
   */
  updateParentCountBadge() {
    // Find the parent disclosure component
    const disclosureComponent = this.element.closest(
      '[data-controller="objective-disclosure"]',
    );

    if (!disclosureComponent) return;

    // Find the trigger button
    const trigger = disclosureComponent.querySelector(
      '[data-objective-disclosure-target="trigger"]',
    );

    if (!trigger) return;

    // Find the badge span
    const badge = trigger.querySelector("span.rounded-full");

    if (!badge) return;

    // Update the badge text with current count
    const newCount = this.selectedValue?.length || 0;
    badge.textContent = newCount;

    // Update badge styling based on count
    if (newCount > 0) {
      badge.classList.remove(
        "bg-neutral-100",
        "text-neutral-500",
        "dark:bg-neutral-800",
        "dark:text-neutral-500",
      );
      badge.classList.add(
        "bg-neutral-200",
        "text-neutral-700",
        "dark:bg-neutral-700",
        "dark:text-neutral-200",
      );
    } else {
      badge.classList.remove(
        "bg-neutral-200",
        "text-neutral-700",
        "dark:bg-neutral-700",
        "dark:text-neutral-200",
      );
      badge.classList.add(
        "bg-neutral-100",
        "text-neutral-500",
        "dark:bg-neutral-800",
        "dark:text-neutral-500",
      );
    }
  }

  /**
   * Handles the 'autocomplete:select' event dispatched by a child controller.
   * Adds the selected value as a new tag.
   *
   * @param {CustomEvent} event - The event containing the selected value.
   *   event.detail.value = display text (e.g., "SANDRA (36)")
   *   event.detail.data = full suggestion object (e.g., { name: "SANDRA", title: "SANDRA (36)" })
   */
  handleSelect(event) {
    const { value, data } = event.detail;
    // Prefer the 'name' property from the data object (without occurrence count)
    // Fall back to value if data.name is not available (for free-form entries)
    const tagName = data?.name || value;
    if (tagName) {
      this.addTag(tagName);
    }
    // Prevent the event from bubbling further if needed.
    event.stopPropagation();
  }

  /**
   * Adds a new tag to the selection.
   * In single-select mode, it replaces the existing value.
   * In multiple-select mode, it adds the new value if it's not already present.
   * Normalizes tags to uppercase to prevent case-sensitive duplicates.
   *
   * @param {string} name - The name of the tag to add.
   */
  addTag(name) {
    const tagName = name.trim().toUpperCase();
    if (!tagName) return;

    if (this.multipleValue) {
      // In multiple mode, add only if it's not a duplicate.
      const currentSelected = new Set(
        this.selectedValue.map((val) => val.toUpperCase()),
      );
      if (!currentSelected.has(tagName)) {
        this.selectedValue = [...this.selectedValue, tagName];
      }
    } else {
      // In single mode, always replace the current selection.
      this.selectedValue = [tagName];
    }

    // Clear the input field for better UX.
    if (this.hasInputTarget) {
      this.inputTarget.value = "";
    }
  }

  /**
   * Removes a tag when its 'X' button is clicked.
   *
   * @param {MouseEvent} event - The click event from the remove button.
   */
  removeTag(event) {
    const nameToRemove = event.currentTarget.dataset.name;
    this.selectedValue = this.selectedValue.filter(
      (name) => name !== nameToRemove,
    );
  }

  /**
   * Main render function. Clears and rebuilds the tag display
   * and syncs the hidden form fields.
   */
  render() {
    this.renderTags();
    this.syncHiddenFields();
  }

  /**
   * Renders the visual tag badges in the tags container.
   */
  renderTags() {
    this.tagsContainerTarget.innerHTML = "";
    this.selectedValue.forEach((name) => {
      this.tagsContainerTarget.insertAdjacentHTML(
        "beforeend",
        this.renderTag(name),
      );
    });
  }

  /**
   * Synchronizes the `selectedValue` array with hidden input fields
   * to ensure they are submitted correctly with the form.
   */
  syncHiddenFields() {
    this.hiddenFieldsTarget.innerHTML = "";
    this.selectedValue.forEach((name) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = this.fieldNameValue;
      input.value = name;
      this.hiddenFieldsTarget.appendChild(input);
    });
  }

  /**
   * Generates the HTML string for a single tag badge.
   *
   * @param {string} name - The name to display in the tag.
   * @returns {string} The HTML string for the tag.
   */
  renderTag(name) {
    return `
      <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-neutral-200 text-neutral-800 dark:bg-neutral-900/50 dark:text-neutral-200 border border-neutral-300 dark:border-neutral-500/50">
        ${this.escapeHTML(name)}
        <button type="button" data-action="click->${this.identifier}#removeTag" data-name="${this.escapeHTML(name)}" class="hover:text-red-500 focus:outline-none">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </span>
    `;
  }

  /**
   * Simple utility to escape HTML to prevent XSS from tag values.
   */
  escapeHTML(str) {
    const p = document.createElement("p");
    p.textContent = str;
    return p.innerHTML;
  }
}
